import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import TextareaAutosize from 'react-textarea-autosize';

const theme = {
  colors: {
    primary: '#E27558',
    primaryLight: '#cff7fc',
    text: '#2D3748',
    textLight: '#718096',
    border: '#E2E8F0',
    background: '#F8FAFC',
    error: '#E53E3E',
    success: '#38A169',
    danger: '#E53E3E',
  },
  shadows: {
    sm: '0 1px 3px rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  },
  radii: {
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '9999px',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.75rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  },
};

export const Section = styled(motion.section)`
  background: white;
  border-radius: ${theme.radii.xl};
  box-shadow: ${theme.shadows.md};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  border: 1px solid ${theme.colors.border};
  position: relative;
  overflow: hidden;
`;

export const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text};
`;

export const Label = styled.label`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
  display: block;
`;

export const Input = styled.input`
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.875rem;
  background: white;
  box-shadow: ${theme.shadows.sm};
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(226, 117, 88, 0.2);
  }
`;

export const Select = styled.select`
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.875rem;
  background: white;
  box-shadow: ${theme.shadows.sm};
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${theme.spacing.sm} center;
  background-size: 1em;
  width: 100%;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(226, 117, 88, 0.2);
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.875rem;
  min-height: ${props => props.rows ? `${props.rows * 24}px` : '100px'};
  resize: vertical;
  background: white;
  box-shadow: ${theme.shadows.sm};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(226, 117, 88, 0.2);
  }
`;

export const AutoTextArea = styled(TextareaAutosize)`
  width: 100%;
  padding: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: 0.875rem;
  background: white;
  box-shadow: ${theme.shadows.sm};
  resize: none;
  overflow: hidden;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(226, 117, 88, 0.2);
  }
`;

export const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, #d45a3b 100%);
  color: white;
  border: none;
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.sm};

  &:hover {
    box-shadow: 0 2px 8px rgba(226, 117, 88, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    margin-right: ${theme.spacing.xs};
  }
`;

export const SecondaryButton = styled(motion.button)`
  background: white;
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  box-shadow: ${theme.shadows.sm};
  margin-bottom: ${theme.spacing.sm};

  &:hover {
    background: ${theme.colors.background};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    transform: translateY(-1px);
  }

  svg {
    margin-right: ${theme.spacing.xs};
  }
`;

export const DangerButton = styled(motion.button)`
  background: ${theme.colors.danger};
  color: white;
  border: none;
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: #c53030;
    transform: translateY(-1px);
  }

  svg {
    margin-right: ${theme.spacing.xs};
  }
`;

export const RemoveButton = styled(DangerButton)`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 0.75rem;
  border-radius: ${theme.radii.md};
`;

export const DesignItem = styled.div`
  background: ${theme.colors.primaryLight};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  box-shadow: ${theme.shadows.sm};
`;

export const DesignCodeInputContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  align-items: center;
  width: 100%;
`;

export const DesignPreviewContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-left: ${theme.spacing.md};
`;

export const DesignPreviewLabel = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
`;

export const Thumbnail = styled(motion.img)`
  width: 80px;
  height: 80px;
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border};
  object-fit: contain;
  background: white;
  box-shadow: ${theme.shadows.sm};
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    transform: scale(1.05);
    box-shadow: ${theme.shadows.md};
  }
`;

export const ThumbnailPlaceholder = styled(motion.div)`
  width: 80px;
  height: 80px;
  border-radius: ${theme.radii.md};
  border: 1px dashed ${theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: ${theme.colors.textLight};
  text-align: center;
  padding: ${theme.spacing.xs};
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: ${theme.radii.md};
  background: ${theme.colors.background};
`;

export const SuccessBadge = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: ${theme.colors.success};
  color: white;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.radii.full};
  font-size: 0.75rem;
  font-weight: 600;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
`;

export const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${theme.spacing.lg};
  gap: ${theme.spacing.sm};
`;

export const CompactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  align-items: flex-start;
`;

export const ApparelItem = styled(motion.div)`
  border-left: 6px solid ${theme.colors.primary};
  border-radius: ${theme.radii.xl};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
  background: white;
  box-shadow: ${theme.shadows.sm};
  position: relative;
  flex: 1;
  min-width: 300px;
  margin: ${theme.spacing.sm};

  &:nth-of-type(even) {
    background: ${theme.colors.background};
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${theme.colors.primary} 0%, ${theme.colors.border} 100%);
  }
`;

export const ApparelGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -${theme.spacing.sm};
`;